import { Token } from '@coast/schemas';
import { isEmpty } from 'lodash';
import React, { useContext } from 'react';
import { View } from '@shared/components/View';
import { MBIconV2, MBUserImage } from '@shared/components/image';
import { MBSelectableDot } from '@shared/components/list';
import { MBScrollView } from '@shared/components/scrollview';
import { MBSearchBar } from '@shared/components/searchBar';
import { MBTouchableOpacity } from '@shared/components/touchable';
import { MBTextBody1, MBTextOverlineBold } from '@shared/components/typography';
import { ColorThemeContext, grid } from '@shared/styles';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { DefaultPersonSelectionHooks } from './hooks/DefaultPersonSelectionHooks';

interface Props {
  hooks: DefaultPersonSelectionHooks;
}

export function DefaultPersonSelection({ hooks }: Props) {
  const colors = useContext(ColorThemeContext);

  return (
    <View paddingBottom={2}>
      <View paddingHorizontal={3} paddingVertical={2}>
        <MBTextOverlineBold color={colors.foregroundInactive}>
          {i18n('workflowTemplate.responseOptions')}
        </MBTextOverlineBold>
      </View>
      <View paddingHorizontal={3} paddingBottom={2}>
        <MBSearchBar
          value={hooks.currentFilter}
          onChangeText={hooks.onFilter}
          placeholder={i18n(`builder.defaultPerson.searchPlaceholder`)}
        />
      </View>
      <MBScrollView style={{ maxHeight: grid(30) }}>
        {isEmpty(hooks.currentFilter) && (
          <MBTouchableOpacity onPress={hooks.onMeSelected}>
            <View row centerVertically paddingVertical={0.5} paddingLeft={2} paddingRight={3}>
              <MBIconV2 name="avatar" color={colors.accent} />
              <View fill centerVertically paddingLeft={2}>
                <MBTextBody1>{i18n('builder.defaultPerson.meSelection')}</MBTextBody1>
              </View>
              <MBSelectableDot selected={hooks.selectedUsers?.token === Token.ME} radio={false} />
            </View>
          </MBTouchableOpacity>
        )}
        {hooks.filteredUsers?.map(user => {
          return (
            <React.Fragment key={user.id}>
              <SelectPersonRow
                user={user}
                isSelected={!!hooks.selectedUsers?.userIds?.includes(user.id)}
                onPress={() => {
                  hooks.onPersonSelected(user.id);
                }}
              />
            </React.Fragment>
          );
        })}
      </MBScrollView>
    </View>
  );
}

interface PersonSelectionRowProps {
  user: API.ShortUserFragment;
  isSelected: boolean;
  onPress(userId: number);
}

function SelectPersonRow({ user, isSelected, onPress }: PersonSelectionRowProps) {
  return (
    <MBTouchableOpacity onPress={() => onPress(user.id)}>
      <View row centerVertically paddingVertical={0.5} paddingHorizontal={3}>
        <MBUserImage
          user={{
            id: user.id,
            fullName: user.fullName,
            username: '',
            imageUrl: user.imageUrl,
            isMe: false,
            isPending: false,
            lastRemindedAt: null,
          }}
          style={{
            width: 24,
            height: 24,
            borderRadius: 12,
            overflow: 'hidden',
            marginRight: grid(2),
          }}
          size={'small'}
        />
        <View fill centerVertically paddingLeft={1}>
          <MBTextBody1>{user.fullName}</MBTextBody1>
        </View>
        <MBSelectableDot selected={isSelected} radio={false} />
      </View>
    </MBTouchableOpacity>
  );
}
